import { VoxelAsset, VoxelData } from '../voxelTypes';
import { PALETTE, getRandomizedColor } from '../voxelColor';

export function generateVoxelLanternPole(id: string, name: string): VoxelAsset {
  const voxels: VoxelData[] = [];

  // Sleek, delicate stone base (y = 0)
  voxels.push({ x: 0, y: 0, z: 0, color: PALETTE.STONE_DARK, materialType: 'opaque' });

  // Slender dark iron post (y = 1)
  voxels.push({ x: 0, y: 1, z: 0, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque' });

  // Small golden lantern lamp cap & glass glow (y = 2)
  voxels.push({ x: 0, y: 2, z: 0, color: PALETTE.LAMP_GOLD, materialType: 'emissive' });
  // Iron hat on lantern top (y = 3)
  voxels.push({ x: 0, y: 3, z: 0, color: PALETTE.STONE_DARK, materialType: 'opaque' });

  return {
    id,
    name,
    category: 'prop',
    description: 'Miniature rustic streetlamp lantern pole',
    voxels,
    dimensions: { x: 1, y: 4, z: 1 }
  };
}

export function generateVoxelWell(id: string, name: string): VoxelAsset {
  const voxels: VoxelData[] = [];

  // Square Cobblestone Basin (4x4 outer, 2x2 inner water)
  for (let x = -2; x <= 2; x += 0.5) {
    for (let z = -2; z <= 2; z += 0.5) {
      const isRim = Math.abs(x) >= 1.4 || Math.abs(z) >= 1.4;
      if (isRim) {
        voxels.push({
          x, y: 0.1, z,
          color: PALETTE.STONE_DARK,
          materialType: 'opaque',
          scale: 0.5
        });
        voxels.push({
          x, y: 0.6, z,
          color: PALETTE.STONE_MEDIUM,
          materialType: 'opaque',
          scale: 0.5
        });
      } else {
        // Deep shimmer water
        voxels.push({
          x, y: 0.2, z,
          color: PALETTE.WATER_CYAN,
          materialType: 'water',
          scale: 0.5
        });
      }
    }
  }

  // 4 Corner Timber Support Posts
  const postCoords = [
    { x: -1.6, z: -1.6 },
    { x: 1.6, z: -1.6 },
    { x: -1.6, z: 1.6 },
    { x: 1.6, z: 1.6 }
  ];

  postCoords.forEach(p => {
    for (let py = 0.6; py <= 2.6; py += 0.5) {
      voxels.push({
        x: p.x, y: py, z: p.z,
        color: PALETTE.WOOD_OAK_DARK,
        materialType: 'opaque',
        scale: 0.4
      });
    }
  });

  // Winding Rope Spool & Crank Handle
  for (let sx = -1.4; sx <= 1.4; sx += 0.2) {
    const isSpool = Math.abs(sx) < 0.6;
    voxels.push({
      x: sx, y: 2.1, z: 0,
      color: isSpool ? PALETTE.THATCH_YELLOW : PALETTE.WOOD_OAK_DARK,
      materialType: 'opaque',
      scale: isSpool ? 0.35 : 0.2
    });
  }
  // Iron Crank Handle
  voxels.push({ x: 1.6, y: 2.1, z: 0.3, color: PALETTE.STONE_DARK, materialType: 'opaque', scale: 0.25 });
  voxels.push({ x: 1.6, y: 1.8, z: 0.3, color: PALETTE.STONE_DARK, materialType: 'opaque', scale: 0.25 });

  // Wooden Bucket hanging on rope
  voxels.push({ x: 0, y: 1.2, z: 0, color: PALETTE.WOOD_OAK_MEDIUM, materialType: 'opaque', scale: 0.45 });
  voxels.push({ x: 0, y: 1.5, z: 0, color: PALETTE.THATCH_YELLOW, materialType: 'opaque', scaleX: 0.1, scaleZ: 0.1, scaleY: 0.5 }); // Rope

  // Shingled Hip Canopy Roof
  for (let rx = -2.2; rx <= 2.2; rx += 0.4) {
    for (let rz = -2.2; rz <= 2.2; rz += 0.4) {
      const distFromCenter = Math.max(Math.abs(rx), Math.abs(rz));
      const roofY = 3.2 - distFromCenter * 0.3;
      voxels.push({
        x: rx, y: roofY, z: rz,
        color: getRandomizedColor(PALETTE.ROOF_RED_CLAY, 0.02),
        materialType: 'opaque',
        scale: 0.45
      });
    }
  }

  return {
    id,
    name,
    category: 'prop',
    description: 'Detailed square stone village well with bucket & canopy',
    voxels,
    dimensions: { x: 6, y: 6, z: 6 }
  };
}

export function generateVoxelWindmill(id: string, name: string): VoxelAsset {
  const voxels: VoxelData[] = [];

  // Taller, majestic octagonal stone & timber lighthouse/windmill tower (height 24)
  const baseRadius = 5;
  const height = 24;

  for (let y = 0; y < height; y++) {
    const currentR = baseRadius - (y / height) * 1.8;
    for (let x = -Math.ceil(currentR); x <= Math.ceil(currentR); x++) {
      for (let z = -Math.ceil(currentR); z <= Math.ceil(currentR); z++) {
        const dist = Math.hypot(x, z);
        if (dist <= currentR && dist >= currentR - 1.2) {
          const isBase = y < 5;
          const isTimberRing = y % 5 === 0;
          let color = isBase ? PALETTE.STONE_MEDIUM : PALETTE.FLOWER_WHITE;
          if (isTimberRing) color = PALETTE.WOOD_OAK_DARK;
          else if (!isBase) color = getRandomizedColor(0xd8d4c8, 0.02);

          voxels.push({ x, y, z, color, materialType: 'opaque' });
        }
      }
    }
  }

  // Windows at multiple levels
  [7, 13, 19].forEach(wy => {
    voxels.push({ x: 0, y: wy, z: Math.floor(baseRadius - (wy / height) * 1.8), color: PALETTE.GLASS_GLOW, materialType: 'emissive' });
  });

  // Observation Gallery / Balcony Deck (y = height - 2) with micro railings
  const deckY = height - 2;
  const deckR = 4.2;
  for (let x = -Math.ceil(deckR); x <= Math.ceil(deckR); x += 0.5) {
    for (let z = -Math.ceil(deckR); z <= Math.ceil(deckR); z += 0.5) {
      const dist = Math.hypot(x, z);
      if (dist <= deckR && dist >= deckR - 0.8) {
        voxels.push({ x, y: deckY, z, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scale: 0.5 });
        if (Math.abs(dist - deckR) < 0.3) {
          voxels.push({ x, y: deckY + 0.6, z, color: PALETTE.WOOD_OAK_MEDIUM, materialType: 'opaque', scale: 0.4 });
        }
      }
    }
  }

  // Conical Roof Peak & Iron Weather Vane
  for (let y = height; y <= height + 6; y++) {
    const r = (height + 6 - y) * 0.7;
    for (let x = -Math.ceil(r); x <= Math.ceil(r); x++) {
      for (let z = -Math.ceil(r); z <= Math.ceil(r); z++) {
        if (Math.hypot(x, z) <= r) {
          voxels.push({
            x, y, z,
            color: getRandomizedColor(PALETTE.ROOF_RED_CLAY, 0.02),
            materialType: 'opaque'
          });
        }
      }
    }
  }

  // Iron Weather Vane on top peak
  const topY = height + 6.8;
  voxels.push({ x: 0, y: topY, z: 0, color: PALETTE.STONE_DARK, materialType: 'opaque', scaleX: 0.2, scaleZ: 0.2, scaleY: 0.8 });
  voxels.push({ x: 0, y: topY + 0.6, z: 0, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scale: 0.4 });

  // Center Mounting Collar on Tower Face
  const hubY = height - 4;
  const hubZ = Math.ceil(baseRadius - 3.2);

  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      voxels.push({ x: dx, y: hubY + dy, z: hubZ, color: PALETTE.STONE_DARK, materialType: 'opaque' });
    }
  }

  return {
    id,
    name,
    category: 'prop',
    description: 'Tall majestic octagonal lighthouse / windmill tower',
    voxels,
    dimensions: { x: 24, y: 32, z: 24 }
  };
}

export function generateWindmillSailVoxels(): VoxelData[] {
  const voxels: VoxelData[] = [];
  const bladeLength = 9;

  // Central Hub Cap
  voxels.push({ x: 0, y: 0, z: 0.3, color: PALETTE.LAMP_GOLD, materialType: 'opaque', scale: 0.6 });
  voxels.push({ x: 0, y: 0, z: 0.0, color: PALETTE.STONE_DARK, materialType: 'opaque', scale: 0.4 });

  for (let i = 1; i <= bladeLength; i++) {
    const isLatticeCross = i % 2 === 0;

    // Up blade arm (spars z:[-0.15, 0.15], canvas z:[0.21, 0.29], lattice z:[0.31, 0.39])
    voxels.push({ x: 0, y: i, z: 0, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scaleX: 0.3, scaleY: 1.0, scaleZ: 0.3 });
    voxels.push({ x: 0.55, y: i, z: 0.25, color: PALETTE.FLOWER_WHITE, materialType: 'opaque', scaleX: 0.5, scaleY: 0.85, scaleZ: 0.08 });
    voxels.push({ x: 1.10, y: i, z: 0.25, color: PALETTE.FLOWER_WHITE, materialType: 'opaque', scaleX: 0.5, scaleY: 0.85, scaleZ: 0.08 });
    if (isLatticeCross) {
      voxels.push({ x: 0.55, y: i, z: 0.35, color: PALETTE.WOOD_OAK_MEDIUM, materialType: 'opaque', scaleX: 0.5, scaleY: 0.2, scaleZ: 0.08 });
    }

    // Down blade arm
    voxels.push({ x: 0, y: -i, z: 0, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scaleX: 0.3, scaleY: 1.0, scaleZ: 0.3 });
    voxels.push({ x: -0.55, y: -i, z: 0.25, color: PALETTE.FLOWER_WHITE, materialType: 'opaque', scaleX: 0.5, scaleY: 0.85, scaleZ: 0.08 });
    voxels.push({ x: -1.10, y: -i, z: 0.25, color: PALETTE.FLOWER_WHITE, materialType: 'opaque', scaleX: 0.5, scaleY: 0.85, scaleZ: 0.08 });
    if (isLatticeCross) {
      voxels.push({ x: -0.55, y: -i, z: 0.35, color: PALETTE.WOOD_OAK_MEDIUM, materialType: 'opaque', scaleX: 0.5, scaleY: 0.2, scaleZ: 0.08 });
    }

    // Right blade arm
    voxels.push({ x: i, y: 0, z: 0, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scaleX: 1.0, scaleY: 0.3, scaleZ: 0.3 });
    voxels.push({ x: i, y: 0.55, z: 0.25, color: PALETTE.FLOWER_WHITE, materialType: 'opaque', scaleX: 0.85, scaleY: 0.5, scaleZ: 0.08 });
    voxels.push({ x: i, y: 1.10, z: 0.25, color: PALETTE.FLOWER_WHITE, materialType: 'opaque', scaleX: 0.85, scaleY: 0.5, scaleZ: 0.08 });
    if (isLatticeCross) {
      voxels.push({ x: i, y: 0.55, z: 0.35, color: PALETTE.WOOD_OAK_MEDIUM, materialType: 'opaque', scaleX: 0.2, scaleY: 0.5, scaleZ: 0.08 });
    }

    // Left blade arm
    voxels.push({ x: -i, y: 0, z: 0, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scaleX: 1.0, scaleY: 0.3, scaleZ: 0.3 });
    voxels.push({ x: -i, y: -0.55, z: 0.25, color: PALETTE.FLOWER_WHITE, materialType: 'opaque', scaleX: 0.85, scaleY: 0.5, scaleZ: 0.08 });
    voxels.push({ x: -i, y: -1.10, z: 0.25, color: PALETTE.FLOWER_WHITE, materialType: 'opaque', scaleX: 0.85, scaleY: 0.5, scaleZ: 0.08 });
    if (isLatticeCross) {
      voxels.push({ x: -i, y: -0.55, z: 0.35, color: PALETTE.WOOD_OAK_MEDIUM, materialType: 'opaque', scaleX: 0.2, scaleY: 0.5, scaleZ: 0.08 });
    }
  }

  return voxels;
}

export function generateVoxelCart(id: string, name: string): VoxelAsset {
  const voxels: VoxelData[] = [];

  // Cart Bed (half-size blocks, scale 0.5)
  for (let x = -1; x <= 1; x += 0.5) {
    for (let z = -0.5; z <= 0.5; z += 0.5) {
      voxels.push({ x, y: 0.5, z, color: getRandomizedColor(PALETTE.WOOD_OAK_MEDIUM, 0.02), materialType: 'opaque', scale: 0.5 });
    }
  }

  // Side slat walls (half-size blocks)
  for (let x = -1; x <= 1; x += 0.5) {
    voxels.push({ x, y: 1.0, z: -0.5, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scale: 0.5 });
    voxels.push({ x, y: 1.0, z: 0.5, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scale: 0.5 });
  }

  // Back end wall
  for (let z = -0.5; z <= 0.5; z += 0.5) {
    voxels.push({ x: -1, y: 1.0, z, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scale: 0.5 });
  }

  // Front shaft handles
  voxels.push({ x: 1.5, y: 0.5, z: -0.5, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scale: 0.5 });
  voxels.push({ x: 1.5, y: 0.5, z: 0.5, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scale: 0.5 });

  // Wheels (half-size blocks)
  [-0.5, 0.5].forEach(wx => {
    voxels.push({ x: wx, y: 0.25, z: -0.8, color: PALETTE.STONE_DARK, materialType: 'opaque', scale: 0.5 });
    voxels.push({ x: wx, y: 0.25, z: 0.8, color: PALETTE.STONE_DARK, materialType: 'opaque', scale: 0.5 });
  });

  // Hay / crop cargo inside cart bed
  voxels.push({ x: -0.5, y: 1.0, z: 0, color: PALETTE.WHEAT_GOLD, materialType: 'foliage', scale: 0.45 });
  voxels.push({ x: 0, y: 1.0, z: 0, color: PALETTE.THATCH_YELLOW, materialType: 'foliage', scale: 0.45 });

  return {
    id,
    name,
    category: 'prop',
    description: 'Compact wooden farm supply cart made with half-size voxel blocks',
    voxels,
    dimensions: { x: 3, y: 2, z: 3 }
  };
}
